import { useState, useEffect } from "react";
import { ChevronRight, ChevronDown, Brain, Loader2 } from "lucide-react";
import { DemoHint } from "@/components/demo/DemoHint";
import { useSpotRate } from "@/hooks/useSpotRate";

type Strategy = "market" | "conservative";

interface AtlasRateCardProps {
  originLat?: number | null;
  originLon?: number | null;
  destLat?: number | null;
  destLon?: number | null;
  laneMiles?: number | null;
  equipmentType?: string | null;
}

function useCountUp(target: number, duration = 500) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function StrategyToggle({
  value,
  onChange,
}: {
  value: Strategy;
  onChange: (s: Strategy) => void;
}) {
  const options: { key: Strategy; label: string }[] = [
    { key: "market", label: "Market" },
    { key: "conservative", label: "Conservative" },
  ];
  return (
    <div className="flex rounded-lg overflow-hidden border border-border">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className="flex-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider transition-all duration-150"
          style={{
            backgroundColor:
              value === o.key ? "hsl(var(--brand-teal-deep))" : "transparent",
            color:
              value === o.key ? "hsl(0 0% 100%)" : "hsl(var(--muted-foreground))",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function AtlasRateCard({
  originLat,
  originLon,
  destLat,
  destLon,
  laneMiles,
  equipmentType,
}: AtlasRateCardProps) {
  const [strategy, setStrategy] = useState<Strategy>("market");
  const [showWhy, setShowWhy] = useState(false);

  const { data: spotResult, isLoading } = useSpotRate({
    originLat,
    originLon,
    destLat,
    destLon,
    laneMiles,
    equipmentType,
  });

  const marketRate = spotResult?.marketRate ?? null;
  const conservativeRate = spotResult?.suggestedPostRate ?? null;

  const activeRate = strategy === "market" ? marketRate : conservativeRate;
  const hasData = activeRate !== null;

  const confidence = spotResult?.confidencePercent ?? 0;

  const animRate = useCountUp(activeRate ?? 0, 500);
  const animConf = useCountUp(confidence, 500);

  const insights = spotResult
    ? [
        `Based on ${spotResult.compCount} comparable live loads.`,
        `Average RPM: $${spotResult.averageRpm.toFixed(2)}/mi across comps.`,
        spotResult.tier === 1
          ? "Comps found within 150-mile radius (high confidence)."
          : "Comps found within 250-mile expanded radius.",
      ]
    : [
        "Enter origin, destination, and equipment to get a rate.",
        "Rates are calculated from live posted loads.",
        "Minimum 3 broker-style comps required.",
      ];

  const whyBullets = spotResult
    ? [
        `Filtered to FTL, 1-pick/1-drop, ${equipmentType || 'matching'} equipment.`,
        `IQR outlier protection applied to ${spotResult.compCount} comps.`,
        strategy === "conservative"
          ? "Conservative adds 9% premium for guaranteed coverage."
          : "Market rate reflects current live comp average.",
      ]
    : [
        "No live comparable loads found for this lane.",
        "Try adjusting equipment type or lane.",
        "Rate will appear when sufficient data exists.",
      ];

  const noInput = !originLat || !destLat || !laneMiles || !equipmentType;

  return (
    <div
      className="rounded-xl flex flex-col overflow-hidden bg-card border border-border"
      style={{
        borderTop: "2px solid hsl(var(--brand-teal-deep))",
        boxShadow: "var(--shadow-md), 0 0 24px hsl(170 85% 32% / 0.08)",
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <DemoHint hintKey="section.atlasRate" side="bottom">
            <h4 className="text-sm font-bold uppercase tracking-widest text-foreground">
              ATLAS AI Engine
            </h4>
          </DemoHint>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: hasData ? "hsl(var(--brand-teal-subtle))" : "hsl(var(--muted))",
            color: hasData ? "hsl(var(--brand-teal-deep))" : "hsl(var(--muted-foreground))",
          }}
        >
          {isLoading ? "Analyzing…" : hasData ? "Active" : noInput ? "Waiting" : "No Data"}
        </span>
      </div>

      <div className="px-4 py-3 space-y-3 flex-1 flex flex-col">
        <StrategyToggle value={strategy} onChange={setStrategy} />

        <div
          className="rounded-lg p-4 relative overflow-hidden"
          style={{
            background: hasData
              ? "linear-gradient(135deg, hsl(170 60% 97%), hsl(170 50% 94%))"
              : "hsl(var(--muted))",
            border: hasData
              ? "1px solid hsl(170 85% 32% / 0.15)"
              : "1px solid hsl(var(--border))",
          }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-4 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: "hsl(var(--brand-teal-deep))" }} />
              <span className="text-sm text-muted-foreground">Analyzing lane comps…</span>
            </div>
          ) : hasData ? (
            <>
              <div className="flex items-baseline gap-3 relative">
                <p
                  className="text-5xl font-extrabold tracking-tight"
                  style={{ color: "hsl(var(--brand-teal-deep))" }}
                >
                  ${animRate.toLocaleString()}
                </p>
                <span
                  className="rounded-full px-2.5 py-0.5 text-sm font-extrabold"
                  style={{
                    backgroundColor: "hsl(var(--brand-teal-subtle))",
                    color: "hsl(var(--brand-teal-deep))",
                  }}
                >
                  {animConf}%
                </span>
              </div>
              <p className="text-[10px] mt-1 relative text-muted-foreground">
                Confidence Score · {spotResult?.compCount} comps · Tier {spotResult?.tier}
              </p>
            </>
          ) : (
            <div className="py-3 text-center">
              <p className="text-sm font-semibold text-muted-foreground">
                {noInput ? "Enter lane details" : "Insufficient comp data"}
              </p>
              <p className="text-[10px] text-muted-foreground/70 mt-1">
                {noInput ? "Origin, destination & equipment needed" : "Fewer than 3 valid comps found"}
              </p>
            </div>
          )}
        </div>

        <ul className="space-y-1.5 flex-1">
          {insights.map((t) => (
            <li
              key={t}
              className="flex items-start gap-2 text-[11px] text-muted-foreground transition-opacity duration-200"
            >
              <span
                className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
              />
              {t}
            </li>
          ))}
        </ul>

        <button
          onClick={() => setShowWhy(!showWhy)}
          className="flex items-center gap-1 text-[10px] font-semibold text-foreground"
        >
          Why this rate?
          {showWhy ? (
            <ChevronDown className="h-3 w-3" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          ) : (
            <ChevronRight className="h-3 w-3" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          )}
        </button>

        <div
          className="overflow-hidden transition-all duration-200"
          style={{
            maxHeight: showWhy ? 100 : 0,
            opacity: showWhy ? 1 : 0,
          }}
        >
          <ul className="space-y-1 pb-1">
            {whyBullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2 text-[10px] text-muted-foreground"
              >
                <span className="mt-1 h-1 w-1 rounded-full flex-shrink-0 bg-muted-foreground/40" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
